import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, RefreshCw, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import DatePicker from '../components/DatePicker';
import FloatingField from '../components/FloatingField';
import Dropdown from '../components/Dropdown';
import TimeSlotPicker from '../components/TimeSlotPicker';
import AddressWithoutStreetConfirmModal from '../components/AddressWithoutStreetConfirmModal';
import { API_URL } from '../config';
import useToast from '../hooks/useToast';
import { createTask, getTaskAssignees } from '../services/tasksApi';
import { safeFetch } from '../utils/safeFetch';
import { haptic } from '../utils/vibration';
import { mergeAddressSuggestionCoordinates, resolvePreferredAddressCoordinates } from '../utils/addressCoordinates';
import { getServiceAddressValidation, incompleteServiceAddressMessage, isCompleteServiceAddress } from '../utils/serviceAddressValidation';

const PRIVILEGED_TYPES = new Set(['dispatcher', 'admin', 'developer']);

const initialDraft = {
  scheduled_date: '',
  time_slot: '',
  time_mode: 'interval',
  interval_slot: '',
  exact_hour: '09',
  exact_minute: '00',
  address: '',
  selectedAddress: null,
  description: '',
  requires_photo: false,
  assigned_to: '',
};

const TIME_MODE_OPTIONS = [
  { id: 'exact', label: 'Конкретное время' },
  { id: 'interval', label: 'Интервал' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
const MINUTE_OPTIONS = Array.from({ length: 6 }, (_, index) => String(index * 10).padStart(2, '0'));
const TASK_DESCRIPTION_MAX_LENGTH = 500;

const getSuggestionAddressText = (suggestion) => suggestion?.value || suggestion?.unrestricted_value || '';

const buildTaskTimeSlot = (draft) => {
  if (draft.time_mode === 'exact') {
    if (!draft.exact_hour || !draft.exact_minute) {
      return '';
    }
    return `${draft.exact_hour}:${draft.exact_minute}`;
  }

  return draft.interval_slot || '';
};

const getShortAddressLabel = (address) => {
  if (!address) return '';

  const parts = String(address)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length <= 3) {
    return address;
  }

  return parts.slice(-3).join(', ');
};

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const roundMinutesUpToStep = (value, step) => {
  const normalized = Math.max(0, Number(value) || 0);
  return Math.ceil(normalized / step) * step;
};

const NewTaskPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast, showToast, hideToast } = useToast(3200);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const returnTab = searchParams.get('tab') || 'all';

  const [auth, setAuth] = useState({
    loaded: false,
    userType: localStorage.getItem('user_type') || 'user',
  });
  const [taskDraft, setTaskDraft] = useState(initialDraft);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressWithoutStreetSuggestion, setAddressWithoutStreetSuggestion] = useState(null);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [isAddressEditing, setIsAddressEditing] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [yandexApiKey, setYandexApiKey] = useState('');

  const addressRequestIdRef = useRef(0);
  const addressCoordinateRequestRef = useRef(0);
  const addressDropdownRef = useRef(null);
  const addressInputRef = useRef(null);

  const isPrivileged = PRIVILEGED_TYPES.has(String(auth.userType || '').toLowerCase());
  const assigneeOptions = useMemo(
    () => [
      { value: '', label: 'Оставить в доступных' },
      ...assignees.map((user) => ({
        value: String(user.id),
        label: user.name,
      })),
    ],
    [assignees]
  );

  const displayedAddressValue = !isAddressEditing && taskDraft.selectedAddress
    ? getShortAddressLabel(getSuggestionAddressText(taskDraft.selectedAddress))
    : taskDraft.address;
  const todayKey = useMemo(() => formatDateKey(new Date()), []);
  const isTodaySelected = taskDraft.scheduled_date === todayKey;
  const disabledIntervalMinutes = useMemo(() => {
    if (!isTodaySelected) return null;
    const now = new Date();
    return (now.getHours() * 60) + now.getMinutes();
  }, [isTodaySelected]);
  const exactHourOptions = useMemo(() => {
    if (!isTodaySelected) return HOUR_OPTIONS;
    const now = new Date();
    return HOUR_OPTIONS.filter((hour) => Number(hour) >= now.getHours());
  }, [isTodaySelected]);
  const exactMinuteOptions = useMemo(() => {
    if (!isTodaySelected) return MINUTE_OPTIONS;
    const now = new Date();
    const selectedHour = Number(taskDraft.exact_hour || 0);
    if (selectedHour > now.getHours()) {
      return MINUTE_OPTIONS;
    }
    if (selectedHour < now.getHours()) {
      return [];
    }
    const minMinute = roundMinutesUpToStep(now.getMinutes(), 10);
    if (minMinute >= 60) {
      return [];
    }
    return MINUTE_OPTIONS.filter((minute) => Number(minute) >= minMinute);
  }, [isTodaySelected, taskDraft.exact_hour]);

  useEffect(() => {
    let cancelled = false;
    safeFetch(`${API_URL}/api/settings/yandex_maps_api_key`)
      .then((data) => {
        if (!cancelled) setYandexApiKey(data?.value || '');
      })
      .catch(() => {
        if (!cancelled) setYandexApiKey('');
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAuth = async () => {
      try {
        const data = await safeFetch(`${API_URL}/api/check-auth`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (cancelled) return;

        const userType = data.user_type || 'user';
        setAuth({
          loaded: true,
          userType,
        });

        if (!PRIVILEGED_TYPES.has(String(userType).toLowerCase())) {
          navigate(`/tasks?tab=${returnTab}`, { replace: true });
        }
      } catch (fetchError) {
        if (cancelled) return;
        setAuth((prev) => ({ ...prev, loaded: true }));
        showToast(fetchError.message || 'Не удалось загрузить доступы', 'error');
      }
    };

    loadAuth();

    return () => {
      cancelled = true;
    };
  }, [navigate, returnTab]);

  useEffect(() => {
    if (!auth.loaded || !isPrivileged) {
      return;
    }

    let cancelled = false;

    const loadAssignees = async () => {
      setAssigneesLoading(true);
      try {
        const data = await getTaskAssignees();
        if (!cancelled) {
          setAssignees(Array.isArray(data) ? data : []);
        }
      } catch (fetchError) {
        if (!cancelled) {
          showToast(fetchError.message || 'Не удалось загрузить исполнителей', 'error');
        }
      } finally {
        if (!cancelled) {
          setAssigneesLoading(false);
        }
      }
    };

    loadAssignees();

    return () => {
      cancelled = true;
    };
  }, [auth.loaded, isPrivileged, showToast]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (
        addressDropdownRef.current
        && !addressDropdownRef.current.contains(event.target)
        && addressInputRef.current
        && !addressInputRef.current.contains(event.target)
      ) {
        setShowAddressSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);

  useEffect(() => {
    if (!isTodaySelected) return;
    if (taskDraft.time_mode === 'exact') {
      if (!exactHourOptions.includes(taskDraft.exact_hour)) {
        const nextHour = exactHourOptions[0] || '';
        setTaskDraft((prev) => {
          const next = {
            ...prev,
            exact_hour: nextHour,
            exact_minute: '',
          };
          next.time_slot = buildTaskTimeSlot(next);
          return next;
        });
        return;
      }
      if (!exactMinuteOptions.includes(taskDraft.exact_minute)) {
        const nextMinute = exactMinuteOptions[0] || '';
        setTaskDraft((prev) => {
          const next = {
            ...prev,
            exact_minute: nextMinute,
          };
          next.time_slot = buildTaskTimeSlot(next);
          return next;
        });
      }
      return;
    }

    if (taskDraft.interval_slot) {
      const [hours, minutes] = String(taskDraft.interval_slot).split('-')[0].split(':').map(Number);
      const slotStartMinutes = (hours * 60) + minutes;
      if (disabledIntervalMinutes !== null && slotStartMinutes <= disabledIntervalMinutes) {
        setTaskDraft((prev) => ({
          ...prev,
          interval_slot: '',
          time_slot: '',
        }));
      }
    }
  }, [
    disabledIntervalMinutes,
    exactHourOptions,
    exactMinuteOptions,
    isTodaySelected,
    taskDraft.exact_hour,
    taskDraft.exact_minute,
    taskDraft.interval_slot,
    taskDraft.time_mode,
  ]);

  const handleFieldValue = (field, value) => {
    const normalizedValue = field === 'description'
      ? String(value || '').slice(0, TASK_DESCRIPTION_MAX_LENGTH)
      : value;
    setTaskDraft((prev) => ({
      ...prev,
      [field]: normalizedValue,
    }));

    setValidationErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleTimeModeChange = (mode) => {
    setTaskDraft((prev) => {
      const next = {
        ...prev,
        time_mode: mode,
      };
      next.time_slot = buildTaskTimeSlot(next);
      return next;
    });

    setValidationErrors((prev) => {
      if (!prev.time_slot) {
        return prev;
      }
      const next = { ...prev };
      delete next.time_slot;
      return next;
    });
  };

  const handleTimeValueChange = (field, value) => {
    setTaskDraft((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };
      next.time_slot = buildTaskTimeSlot(next);
      return next;
    });

    setValidationErrors((prev) => {
      if (!prev.time_slot) {
        return prev;
      }
      const next = { ...prev };
      delete next.time_slot;
      return next;
    });
  };

  const fetchAddressSuggestions = async (query) => {
    const trimmed = String(query || '').trim();
    const requestId = addressRequestIdRef.current + 1;
    addressRequestIdRef.current = requestId;

    if (trimmed.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/dadata/address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ query: trimmed, count: 6 }),
      });

      const data = await response.json().catch(() => ([]));
      if (requestId !== addressRequestIdRef.current) {
        return;
      }

      const suggestions = Array.isArray(data) ? data : (data.suggestions || []);
      setAddressSuggestions(suggestions);
      setShowAddressSuggestions(suggestions.length > 0);
    } catch (error) {
      if (requestId !== addressRequestIdRef.current) {
        return;
      }
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
  };

  const handleAddressChange = (value) => {
    addressCoordinateRequestRef.current += 1;
    handleFieldValue('address', value);
    setTaskDraft((prev) => ({
      ...prev,
      address: value,
      selectedAddress: null,
    }));
    fetchAddressSuggestions(value);
  };

  const handleFieldBlur = (field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));

    let error = '';

    if (field === 'scheduled_date' && !taskDraft.scheduled_date) {
      error = 'Выберите дату';
    }

    if (field === 'time_slot' && !taskDraft.time_slot) {
      error = 'Выберите время';
    }

    if (field === 'address' && !taskDraft.selectedAddress) {
      error = 'Выберите адрес из предложенных вариантов';
    } else if (field === 'address' && !isCompleteServiceAddress(taskDraft.selectedAddress)) {
      error = incompleteServiceAddressMessage();
    }

    if (field === 'description' && !String(taskDraft.description || '').trim()) {
      error = 'Опишите задание';
    }

    if (field === 'description' && String(taskDraft.description || '').trim().length > TASK_DESCRIPTION_MAX_LENGTH) {
      error = `Не более ${TASK_DESCRIPTION_MAX_LENGTH} символов`;
    }

    if (error) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: error,
      }));
    }
  };

  const applyAddressSuggestion = (suggestion) => {
    haptic('tap');
    const addressText = getSuggestionAddressText(suggestion);
    const requestId = ++addressCoordinateRequestRef.current;
    setTaskDraft((prev) => ({
      ...prev,
      address: addressText,
      selectedAddress: suggestion,
    }));
    resolvePreferredAddressCoordinates({ apiKey: yandexApiKey, addressText, data: suggestion?.data || {} })
      .then((coordinates) => {
        if (requestId !== addressCoordinateRequestRef.current) return;
        const enriched = mergeAddressSuggestionCoordinates(suggestion, coordinates);
        setTaskDraft((prev) => (
          prev.address === addressText ? { ...prev, selectedAddress: enriched } : prev
        ));
      });
    setValidationErrors((prev) => {
      if (!prev.address) {
        return prev;
      }
      const next = { ...prev };
      delete next.address;
      return next;
    });
    setShowAddressSuggestions(false);
    setIsAddressEditing(false);

    setTimeout(() => {
      addressInputRef.current?.blur();
      document.activeElement?.blur?.();
    }, 50);
  };

  const selectAddress = (suggestion) => {
    const validation = getServiceAddressValidation(suggestion);
    if (validation.isComplete && !validation.hasStreet) {
      setAddressWithoutStreetSuggestion(suggestion);
      return;
    }
    applyAddressSuggestion(suggestion);
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!taskDraft.scheduled_date) {
      nextErrors.scheduled_date = 'Выберите дату';
    }

    if (!taskDraft.time_slot) {
      nextErrors.time_slot = 'Выберите время';
    }

    if (!taskDraft.selectedAddress) {
      nextErrors.address = 'Выберите адрес из предложенных вариантов';
    } else if (!isCompleteServiceAddress(taskDraft.selectedAddress)) {
      nextErrors.address = incompleteServiceAddressMessage();
    }

    if (!String(taskDraft.description || '').trim()) {
      nextErrors.description = 'Опишите задание';
    } else if (String(taskDraft.description || '').trim().length > TASK_DESCRIPTION_MAX_LENGTH) {
      nextErrors.description = `Не более ${TASK_DESCRIPTION_MAX_LENGTH} символов`;
    }

    setValidationErrors(nextErrors);
    setTouchedFields((prev) => ({
      ...prev,
      scheduled_date: true,
      time_slot: true,
      address: true,
      description: true,
    }));

    return Object.keys(nextErrors).length === 0;
  };

  const handleCreateTask = async () => {
    if (!validateForm()) {
      showToast(
        taskDraft.selectedAddress && !isCompleteServiceAddress(taskDraft.selectedAddress)
          ? incompleteServiceAddressMessage()
          : 'Заполните обязательные поля',
        'error'
      );
      return;
    }

    setSubmitting(true);

    try {
      const selectedAddress = taskDraft.selectedAddress;
      const addressText = getSuggestionAddressText(selectedAddress);
      const coordinates = await resolvePreferredAddressCoordinates({
        apiKey: yandexApiKey,
        addressText,
        data: selectedAddress?.data || {}
      });
      const created = await createTask({
        scheduled_date: taskDraft.scheduled_date,
        time_slot: taskDraft.time_slot,
        address_text: addressText,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        dadata_latitude: coordinates.dadataLatitude,
        dadata_longitude: coordinates.dadataLongitude,
        yandex_latitude: coordinates.yandexLatitude,
        yandex_longitude: coordinates.yandexLongitude,
        description: String(taskDraft.description || '').trim(),
        requires_photo: Boolean(taskDraft.requires_photo),
        assigned_to: taskDraft.assigned_to ? Number(taskDraft.assigned_to) : null,
      });

      haptic('success');
      navigate(`/tasks/${created.id}?from=tasks&tab=${returnTab}`, { replace: true });
    } catch (requestError) {
      showToast(requestError.message || 'Не удалось создать поручение', 'error');
      haptic('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    haptic('close');
    navigate(`/tasks?tab=${returnTab}`, { replace: true });
  };

  return (
    <div className="space-y-4 desktop-content-container">
      <div className="space-y-4 rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <DatePicker
              value={taskDraft.scheduled_date}
              onChange={(value) => handleFieldValue('scheduled_date', value)}
              onClose={() => handleFieldBlur('scheduled_date')}
              minDate={todayKey}
              inputClassName="px-4 py-2.5 pl-10 text-[15px] placeholder:text-[14px]"
            />
            {touchedFields.scheduled_date && validationErrors.scheduled_date ? (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.scheduled_date}</p>
            ) : null}
          </div>

          <div>
            <label className="mb-2 block text-[13px] font-medium text-gray-700 dark:text-gray-200">Время</label>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1 dark:bg-gray-800">
                {TIME_MODE_OPTIONS.map((option) => {
                  const isActive = taskDraft.time_mode === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleTimeModeChange(option.id)}
                      className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>

              {taskDraft.time_mode === 'exact' ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <TimeSlotPicker
                      value={taskDraft.exact_hour}
                      onChange={(value) => handleTimeValueChange('exact_hour', value)}
                      options={exactHourOptions}
                      placeholder="Часы"
                      modalTitle="Выберите часы"
                      inputClassName="px-4 py-2.5 pl-10 text-[15px] placeholder:text-[14px]"
                    />
                  </div>
                  <div>
                    <TimeSlotPicker
                      value={taskDraft.exact_minute}
                      onChange={(value) => handleTimeValueChange('exact_minute', value)}
                      options={exactMinuteOptions}
                      placeholder="Минуты"
                      modalTitle="Выберите минуты"
                      inputClassName="px-4 py-2.5 pl-10 text-[15px] placeholder:text-[14px]"
                    />
                  </div>
                </div>
              ) : (
                <TimeSlotPicker
                  value={taskDraft.interval_slot}
                  onChange={(value) => handleTimeValueChange('interval_slot', value)}
                  placeholder="Выберите интервал"
                  slotHours={3}
                  endHour={22}
                  disabledBeforeMinutes={disabledIntervalMinutes}
                  inputClassName="px-4 py-2.5 pl-10 text-[15px] placeholder:text-[14px]"
                />
              )}
            </div>
            {touchedFields.time_slot && validationErrors.time_slot ? (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.time_slot}</p>
            ) : null}
          </div>
        </div>

        <div className="relative">
          <FloatingField label="Адрес" error={touchedFields.address && !!validationErrors.address}>
            <div className="relative">
              <input
              ref={addressInputRef}
              type="text"
              value={displayedAddressValue}
              onChange={(event) => handleAddressChange(event.target.value)}
              onFocus={(event) => {
                setIsAddressEditing(true);
                if (taskDraft.selectedAddress && taskDraft.address !== getSuggestionAddressText(taskDraft.selectedAddress)) {
                  setTaskDraft((prev) => ({
                    ...prev,
                    address: getSuggestionAddressText(prev.selectedAddress),
                  }));
                }
                if (!taskDraft.selectedAddress && addressSuggestions.length > 0) {
                  setShowAddressSuggestions(true);
                }
                setTimeout(() => {
                  event.target.setSelectionRange(event.target.value.length, event.target.value.length);
                  event.target.scrollLeft = event.target.scrollWidth;
                }, 0);
              }}
              onBlur={() => {
                setIsAddressEditing(false);
                handleFieldBlur('address');
              }}
              className={`w-full rounded-xl border px-4 py-2.5 pr-12 text-[15px] placeholder:text-[14px] placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-colors ${
                touchedFields.address && validationErrors.address
                  ? 'border-red-300 focus:ring-red-500 dark:border-red-500 dark:focus:ring-red-500'
                  : isCompleteServiceAddress(taskDraft.selectedAddress)
                    ? 'border-green-300 focus:ring-green-500 dark:border-green-500 dark:focus:ring-green-500'
                    : 'border-gray-300 focus:ring-blue-500 dark:border-gray-700 dark:focus:ring-blue-500'
              } dark:bg-gray-900 dark:text-gray-100`}
              placeholder="Введите адрес..."
            />

              <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center space-x-1">
              {taskDraft.address ? (
                <button
                  type="button"
                  onClick={() => {
                    setTaskDraft((prev) => ({
                      ...prev,
                      address: '',
                      selectedAddress: null,
                    }));
                    setAddressSuggestions([]);
                    setShowAddressSuggestions(false);
                  }}
                  className="flex h-5 w-5 items-center justify-center text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  title="Очистить поле"
                >
                  <X className="h-3 w-3" />
                </button>
              ) : null}
              </div>
            </div>
          </FloatingField>

          {showAddressSuggestions && addressSuggestions.length > 0 ? (
            <div
              ref={addressDropdownRef}
              className="address-suggestions absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-gray-300 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
            >
              {addressSuggestions.map((suggestion, index) => (
                <button
                  key={`${getSuggestionAddressText(suggestion)}-${index}`}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectAddress(suggestion)}
                  className="w-full border-b border-gray-100 px-4 py-3 text-left text-sm last:border-b-0 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none dark:border-gray-700 dark:hover:bg-gray-700 dark:focus:bg-gray-700"
                >
                  <div className="font-medium text-gray-900 dark:text-gray-100">{getSuggestionAddressText(suggestion)}</div>
                  {suggestion.data?.city ? (
                    <div className="text-xs text-gray-500">{suggestion.data.city}</div>
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}

          {touchedFields.address && validationErrors.address ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.address}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Выберите адрес из подсказок, чтобы сохранить координаты для маршрутов.
            </p>
          )}
        </div>

        <div>
          <FloatingField label="Описание задания" error={touchedFields.description && !!validationErrors.description}>
            <textarea
              value={taskDraft.description}
              onChange={(event) => handleFieldValue('description', event.target.value)}
              onBlur={() => handleFieldBlur('description')}
              rows={6}
              maxLength={TASK_DESCRIPTION_MAX_LENGTH}
              placeholder="Что именно нужно сделать"
              className={`w-full rounded-xl border px-4 py-3 text-[15px] outline-none transition-colors focus:ring-2 ${
                touchedFields.description && validationErrors.description
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 dark:border-red-500 dark:focus:border-red-500 dark:focus:ring-red-500/20'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20 dark:border-gray-700 dark:focus:border-blue-500 dark:focus:ring-blue-500/20'
              } resize-none overflow-hidden dark:bg-gray-900 dark:text-gray-100`}
            />
          </FloatingField>
          <div className="mt-1 text-right text-[11px] text-gray-400 dark:text-gray-500">
            {String(taskDraft.description || '').length}/{TASK_DESCRIPTION_MAX_LENGTH}
          </div>
          {touchedFields.description && validationErrors.description ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.description}</p>
          ) : null}
        </div>
      </div>

      <div className="space-y-4 rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
        <label className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-slate-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-800/60">
          <span className="min-w-0 pr-3">
            <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Обязательное фото</span>
          </span>
          <button
            type="button"
            onClick={() => setTaskDraft((prev) => ({ ...prev, requires_photo: !prev.requires_photo }))}
            className="toggle-switch shrink-0"
            style={{
              '--toggle-bg': taskDraft.requires_photo ? '#2563eb' : '#d1d5db',
              '--toggle-height': '22px',
              '--toggle-width': '42px',
              '--knob-x': taskDraft.requires_photo ? '20px' : '2px',
            }}
            aria-pressed={taskDraft.requires_photo}
          >
            <span className="sr-only">Обязательное фото</span>
            <span className="toggle-switch-knob" />
          </button>
        </label>

        <div>
          <Dropdown
            label="Исполнитель"
            value={taskDraft.assigned_to}
            options={assigneeOptions}
            onChange={(value) => handleFieldValue('assigned_to', value)}
            placeholder="Выберите исполнителя"
            menuLabel="Исполнители"
            disabled={assigneesLoading}
            buttonClassName="py-2.5 text-[15px]"
            preferredDirection="up"
          />
          {assigneesLoading ? (
            <div className="mt-2 inline-flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              Загружаем исполнителей...
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 pb-2">
        <button
          type="button"
          onClick={handleCancel}
          disabled={submitting}
          className="h-10 rounded-lg border-2 border-gray-300 px-3 text-sm font-semibold text-gray-600 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"
        >
          Отмена
        </button>

        <button
          type="button"
          onClick={handleCreateTask}
          disabled={submitting}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Создать поручение
        </button>
      </div>

      
      <AddressWithoutStreetConfirmModal
        suggestion={addressWithoutStreetSuggestion}
        onCancel={() => setAddressWithoutStreetSuggestion(null)}
        onConfirm={() => {
          applyAddressSuggestion(addressWithoutStreetSuggestion);
          setAddressWithoutStreetSuggestion(null);
        }}
      />
    </div>
  );
};

export default NewTaskPage;
